<template>
    <div class="ngdialog-content">
        <dialog-header>
            <h4>{{ $t('systems.notification_settings') }}</h4>
        </dialog-header>

        <form name="defaultForm" novalidate @submit.prevent="submit()" class="form">
            <div class="ngdialog__body">
                <p>
                    <label class="form__label">
                        <input type="checkbox" v-model="formData.notify_owner" />
                        <span>&nbsp;</span>
                        <span
                            class="form__label__text"
                            v-html="
                                $t('systems.notifications_dialog.inform_owner_via_email_and_push')
                            "
                        ></span>
                    </label>
                </p>

                <div class="row">
                    <div class="column-mobile-24">
                        <p class="slab-mobile--tiny--bottom">
                            <strong>{{ $t('systems.notifications_dialog.push_info') }}</strong>
                        </p>

                        <ol>
                            <li>
                                {{ $t('systems.notifications_dialog.push_info_step1') }} (<a
                                    href="https://itunes.apple.com/app/mycomfort/id934329162"
                                    target="_blank"
                                    >iOS</a
                                >,
                                <a
                                    href="https://play.google.com/store/apps/details?id=uk.co.windhager.android"
                                    target="_blank"
                                    >Android</a
                                >)
                            </li>
                            <li>{{ $t('systems.notifications_dialog.push_info_step2') }}</li>
                            <li>{{ $t('systems.notifications_dialog.push_info_step3') }}</li>
                        </ol>

                        <h5>{{ $t('systems.notifications_dialog.emails_for_notifications') }}</h5>

                        <div class="row form__row">
                            <label
                                for="notification_email_level1"
                                class="column-mobile-7 form__label"
                            >
                                {{ $t('systems.notifications_dialog.immediate') }} &#185;
                            </label>

                            <div class="column-mobile-17">
                                <notification-input
                                    ref="level1"
                                    :placeholder="$t('fields.email')"
                                    v-model="formData.level1"
                                />

                                <div v-if="$refs.level1 && $refs.level1.$v.$anyError">
                                    <p class="form__error">
                                        {{ $t('fields.email_e_email') }}
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div class="row form__row">
                            <label for="notification_level2" class="column-mobile-7 form__label">
                                {{ $t('systems.notifications_dialog.after_24h') }}
                            </label>

                            <div class="column-mobile-17">
                                <notification-input
                                    ref="level2"
                                    :placeholder="$t('fields.email')"
                                    v-model="formData.level2"
                                />

                                <div v-if="$refs.level2 && $refs.level2.$v.$anyError">
                                    <p class="form__error">
                                        {{ $t('fields.email_e_email') }}
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div class="row form__row">
                            <label for="notification_level3" class="column-mobile-7 form__label">
                                {{ $t('systems.notifications_dialog.after_48h') }}
                            </label>

                            <div class="column-mobile-17">
                                <notification-input
                                    ref="level3"
                                    :placeholder="$t('fields.email')"
                                    v-model="formData.level3"
                                />

                                <div v-if="$refs.level3 && $refs.level3.$v.$anyError">
                                    <p class="form__error">
                                        {{ $t('fields.email_e_email') }}
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div class="brick-mobile--top">
                            <div class="row">
                                <div class="column-mobile-7 form__label">
                                    {{ $t('systems.notifications_dialog.notify_when') }}
                                </div>

                                <div class="checkbox column-mobile-17">
                                    <div class="brick-mobile--tiny--bottom">
                                        <label class="form__label">
                                            <input
                                                type="checkbox"
                                                v-model="formData.notify_at_info"
                                            />
                                            {{ $t('systems.notifications_dialog.info') }}
                                        </label>
                                    </div>

                                    <div class="brick-mobile--tiny--bottom">
                                        <label class="form__label">
                                            <input
                                                type="checkbox"
                                                v-model="formData.notify_at_error"
                                            />
                                            {{ $t('systems.notifications_dialog.error') }}
                                        </label>
                                    </div>

                                    <div class="brick-mobile--tiny--bottom">
                                        <label class="form__label">
                                            <input
                                                type="checkbox"
                                                v-model="formData.notify_at_alarm"
                                            />
                                            {{ $t('systems.notifications_dialog.alarm') }}
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <p class="text-muted">
                            <small>1) {{ $t('systems.notifications_dialog.delay_note') }}</small>
                        </p>
                    </div>
                </div>
            </div>

            <dialog-buttons :submit-text="$t('buttons.save_changes')" :disabled="loading" />
        </form>
    </div>
</template>

<script>
import * as api from '@/api';
import map from 'lodash/map';
import forEach from 'lodash/forEach';
import reject from 'lodash/reject';
import mapValues from 'lodash/mapValues';
import keyBy from 'lodash/keyBy';
import cloneDeep from 'lodash/cloneDeep';
import DialogHeader from '@/components/DialogHeader';
import DialogButtons from '@/components/DialogButtons';
import NotificationInput from '@/components/NotificationInput';

export default {
    name: 'system-notification-settings',
    components: {
        DialogHeader,
        DialogButtons,
        NotificationInput,
    },
    props: {
        system: {
            type: Object,
            required: true,
        },
    },
    data() {
        let data = {
            formData: {
                notify_owner: false,
                notify_at_info: false,
                notify_at_error: false,
                notify_at_alarm: false,
                level1: [],
                level2: [],
                level3: [],
            },
            loading: false,
        };

        return data;
    },
    created() {
        this.fetchNotificationSettings();
    },
    methods: {
        fetchNotificationSettings() {
            api.systems.notifications({ id: this.system.id }).then(
                response => {
                    let settings = response.data.notification_settings;

                    // format level data for vue
                    for (var i = 1; i <= 3; i++) {
                        if (!settings['level' + i] || !Object.keys(settings['level' + i]).length) {
                            // add empty initial entry
                            settings['level' + i] = [{ email: '', push: false }];
                        } else {
                            // transform level object to array
                            settings['level' + i] = map(settings['level' + i], (value, key) => {
                                return { email: key, push: value.push };
                            });
                        }
                    }

                    forEach(settings, (setting, settingKey) => {
                        this.formData[settingKey] = setting;
                    });
                },
                () => {
                    this.$notify({ type: 'error', text: this.$t('errors.error_occurred') });
                }
            );
        },
        submit() {
            if (
                this.$refs.level1.$v.$invalid ||
                this.$refs.level2.$v.$invalid ||
                this.$refs.level3.$v.$invalid
            ) {
                this.$refs.level1.$v.$touch();
                this.$refs.level2.$v.$touch();
                this.$refs.level3.$v.$touch();
                return;
            }

            let data = cloneDeep(this.formData);

            for (var i = 1; i <= 3; i++) {
                // reformat level data for backend
                data['level' + i] = mapValues(
                    keyBy(reject(data['level' + i], { email: '' }), 'email'),
                    item => {
                        return {
                            push: item.push,
                        };
                    }
                );

                // delete empty levels
                if (!Object.keys(data['level' + i]).length) {
                    delete data['level' + i];
                }
            }

            this.loading = true;

            api.systems
                .updateNotifications({ id: this.system.id, data })
                .then(
                    () => {
                        this.$emit('close');
                    },
                    () => {
                        this.$notify({ type: 'error', text: this.$t('errors.error_occurred') });
                    }
                )
                .catch()
                .then(() => {
                    this.loading = false;
                });
        },
    },
};
</script>
